import React, { useEffect, useCallback, useState } from 'react';
import { debounce } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { fetchOptions } from './queries';
import FormInput from './input/FormInput';

function BarcodeArray({ name, count, unique, query, truncateBarcode, enableAsyncBlock, props: { label, type, placeholder, required, ...rest } }) {
    const {
        watch,
        clearErrors,
        getValues,
        setValue,
        setError,
        formState: { dirtyFields },
        updateSelectedValues,
        setAsyncLock,
    } = useFormContext();
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (!name || !enableAsyncBlock || !setAsyncLock) return;
        setAsyncLock(name, fetching);
        return () => setAsyncLock?.(name, false);
    }, [name, enableAsyncBlock, fetching, setAsyncLock]);

    const emptyArray = Array(count).fill('');

    const checkUniqueness = useCallback(
        debounce(async (values, dirtyValues) => {
            setFetching(true);
            const { endpoint, fields, useLoopback } = query;

            // form up and execute query to check uniqueness of values
            const existing = dirtyValues.map((isDirty, index) => {
                // the `string()` wrapper prevents aqp from automatically casting to a number
                const val = isDirty && values[index];
                const where = { [query.uniqueKey || name]: `string(${val})` };
                if (!!val) {
                    return fetchOptions({ endpoint, where, fields, useLoopback });
                }
                return [];
            });
            const existingCheck = await Promise.all(existing);

            // for any existing error or non-unique value, set error message
            existingCheck.forEach((exist, index) => {
                if (exist?.error) {
                    setError(`${name}.${index}`, { type: 'unique', message: `Could not verify ${label[index]}` });
                }
                if (exist?.length) {
                    setError(`${name}.${index}`, { type: 'unique', message: `${label[index]} is not unique` });
                } else {
                    clearErrors(`${name}.${index}`);
                }
            });
            setFetching(false);
        }, 750),
        []
    );

    useEffect(() => {
        const values = getValues(name);
        updateSelectedValues(name, values);
        const dirty = dirtyFields[name];
        if (unique && !!values && dirty) {
            checkUniqueness(values, dirtyFields[name]);
        }
    }, [...(watch(name) || emptyArray)]);

    const onBlur = (idx) => {
        if (!truncateBarcode) return;
        const values = getValues(name);
        const value = String(values[idx]);
        if (value.length === 18) {
            values[idx] = value.slice(7);
            setValue(name, values);
        }
    };

    return emptyArray.map((_item, index) => (
        <FormInput
            key={`flat-array-key-${index + 1}`}
            name={`${name}.${index}`}
            query={query}
            props={{
                type,
                placeholder,
                label: label[index],
                onBlur: () => onBlur(index),
                required: Array.isArray(required) ? required[index] : required,
                disabled: fetching,
                extraHelperText: fetching ? 'Verifying uniqueness...' : '',
                ...rest,
            }}
            // wrap
        />
    ));
}

export default BarcodeArray;
